import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Bell, CheckCheck, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { AppHeader } from "@/components/site/AppHeader";
import { Button } from "@/components/ui/button";
import { ukDateTime } from "@/lib/format";
import { listMyNotifications, markAllNotificationsRead, markNotificationRead } from "@/lib/notifications.functions";

export const Route = createFileRoute("/_authenticated/owner/notifications")({
  head: () => ({ meta: [{ title: "Owner notifications — Shields" }, { name: "robots", content: "noindex" }] }),
  component: OwnerNotifications,
});

function relatedArea(template: string): "/owner/approvals" | "/owner/automation" | "/owner/payments" | "/owner" {
  if (/approval|application/i.test(template)) return "/owner/approvals";
  if (/agent|automation|failed/i.test(template)) return "/owner/automation";
  if (/payment|refund|payout/i.test(template)) return "/owner/payments";
  return "/owner";
}

function OwnerNotifications() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ["my-notifications"], queryFn: () => listMyNotifications(), retry: false });
  const [pending, setPending] = useState<string | null>(null);
  const [allPending, setAllPending] = useState(false);
  const rows = query.data ?? [];
  const unread = rows.filter((row) => !row.read_at).length;

  async function markOne(id: string) {
    setPending(id);
    try {
      const result = await markNotificationRead({ data: { id } });
      if (!result.ok) throw new Error(result.error ?? "Could not mark this notification read");
      await queryClient.invalidateQueries({ queryKey: ["my-notifications"] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not update notification");
    } finally {
      setPending(null);
    }
  }

  async function markAll() {
    setAllPending(true);
    try {
      const result = await markAllNotificationsRead({});
      if (!result.ok) throw new Error(result.error ?? "Could not mark notifications read");
      await queryClient.invalidateQueries({ queryKey: ["my-notifications"] });
      toast.success("Owner notifications are up to date");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not update notifications");
    } finally {
      setAllPending(false);
    }
  }

  return <div className="min-h-screen bg-background pb-28 text-foreground">
    <AppHeader workspace="owner" />
    <main className="mx-auto max-w-3xl space-y-6 px-4 py-8 sm:px-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div><p className="text-xs font-bold uppercase tracking-[.16em] text-[#8a6019]">Command Centre</p><h1 className="mt-2 text-3xl font-extrabold">Notifications</h1><p className="mt-2 text-sm text-muted-foreground">Private updates for your signed-in Owner account.</p></div>
        {unread > 0 && <Button variant="outline" disabled={allPending} onClick={() => void markAll()}><CheckCheck className="mr-2 size-4" aria-hidden />{allPending ? "Updating…" : "Mark all read"}</Button>}
      </header>
      {query.isLoading ? <p role="status" className="rounded-2xl border border-border bg-card p-6">Loading notifications…</p>
      : query.error ? <div role="alert" className="rounded-2xl border border-border bg-card p-6"><p>Notifications could not be loaded.</p><Button className="mt-3" onClick={() => void query.refetch()}>Retry</Button></div>
      : rows.length === 0 ? <section className="rounded-2xl border border-border bg-card p-10 text-center"><Bell className="mx-auto size-8 text-[#b88737]" aria-hidden /><h2 className="mt-4 font-bold">No notifications yet</h2><p className="mt-2 text-sm text-muted-foreground">Owner approvals and automation activity are still available in the Command Centre.</p><Link to="/owner" className="mt-5 inline-flex min-h-11 items-center rounded-xl border border-border px-4 font-semibold hover:bg-muted">Open Command Centre</Link></section>
      : <ul className="space-y-3">{rows.map((row) => {
        const reference = row.payload && typeof row.payload === "object" && !Array.isArray(row.payload) && "reference" in row.payload && typeof row.payload["reference"] === "string" ? row.payload["reference"] : null;
        return <li key={row.id} className={`rounded-2xl border p-5 ${row.read_at ? "border-border bg-card" : "border-[#ead6ac] bg-[#fffaf0]"}`}>
          <div className="flex items-start gap-3"><span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-white text-[#153e80]"><Bell className="size-5" aria-hidden /></span><div className="min-w-0 flex-1"><div className="flex flex-wrap items-center gap-2"><strong className="text-sm">{row.template.replaceAll("_", " ")}</strong>{!row.read_at && <span className="rounded-full bg-[#f7edd9] px-2 py-0.5 text-xs font-bold text-[#765218]">New</span>}</div><p className="mt-1 text-xs text-muted-foreground">{ukDateTime(row.created_at)}{reference ? ` · ${reference}` : ""}</p></div></div>
          <div className="mt-4 flex flex-wrap items-center gap-3"><Link to={relatedArea(row.template)} className="inline-flex min-h-10 items-center gap-1 text-sm font-bold text-[#153e80] hover:underline">Open related area <ChevronRight className="size-4" aria-hidden /></Link>{!row.read_at && <Button variant="ghost" className="min-h-10" disabled={pending===row.id} onClick={() => void markOne(row.id)}>{pending===row.id ? "Updating…" : "Mark read"}</Button>}</div>
        </li>;
      })}</ul>}
    </main>
  </div>;
}
